import React from 'react';

/**
 * Joy Basket brand mark — an asymmetric market-basket silhouette with a
 * single sprig (the "first thoughtful gift"). Drawn as strokes so it reads
 * as a hand-made object, not a UI icon.
 *
 * Single-color: inherits `currentColor` so it can sit on any background.
 */
export const LogoMark = ({ size = 28, className = '', strokeWidth = 1.6, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
    {...rest}
  >
    {/* Handle — slightly off-axis, leans gently right */}
    <path d="M9.2 13.4 Q15.8 4.6, 23.2 13.4" />
    {/* Rim — a flat ellipse-line, the basket's mouth */}
    <path d="M6.4 13.6 Q16 15.4, 25.8 13.6" />
    {/* Body — a deep, asymmetric crescent */}
    <path d="M7.2 13.8 Q9.6 26.6, 16.2 27 Q23.2 26.4, 24.8 13.8" />
    {/* Weave — three short verticals, hand-paced (not symmetric) */}
    <path d="M11.4 16.8 L11.6 23.4" opacity="0.55" />
    <path d="M16.1 17.4 L16.1 24"   opacity="0.55" />
    <path d="M20.6 16.8 L20.4 23.4" opacity="0.55" />
    {/* Sprig — a single leaf rising above the rim, slightly left of centre */}
    <path d="M14.4 13.4 Q14.6 9.4, 17 7.8" />
    <path d="M17 7.8 Q15.2 9, 14.7 11" opacity="0.7" />
  </svg>
);

/**
 * Wordmark + mark lockup. Use in nav, footer, hero.
 * Type uses Fraunces (display) — "Joy" set tighter & heavier, "Basket"
 * lighter, with a hairline gap. Weighted for ~22–28px height.
 */
export const Logo = ({ className = '', markSize = 30, tone = 'ink' }) => {
  const toneClass =
    tone === 'cream' ? 'text-cream-50'
    : tone === 'clay' ? 'text-clay-700'
    : 'text-ink';

  return (
    <span className={`inline-flex items-center gap-2.5 ${toneClass} ${className}`}>
      <LogoMark size={markSize} />
      <span className="font-display leading-none tracking-[-0.015em]">
        <span className="font-semibold" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 30' }}>
          Joy
        </span>
        <span className="font-light opacity-90 ml-[0.15em]" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}>
          Basket
        </span>
      </span>
    </span>
  );
};

export default Logo;
